// src/components/menus/SlideMenuPanel.tsx
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

type Props = {
  open: boolean;
  onClose: () => void;
};

const PANEL_WIDTH = 420;

const styles = {
  backdrop: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 2000,
    opacity: 1,
    transition: "opacity 160ms ease",
  },
  panel: (open: boolean): React.CSSProperties => ({
    position: "fixed",
    top: 0,
    right: 0,
    height: "100vh",
    width: "min(88vw, 420px)",
    maxWidth: PANEL_WIDTH,
    background: "#0f1a24",
    color: "#eef4ff",
    zIndex: 2001,
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 0 24px rgba(0,0,0,0.45)",
    transform: `translateX(${open ? 0 : 100}%)`,
    transition: "transform 200ms ease",
  }),
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 14px",
    borderBottom: "1px solid rgba(255,255,255,0.12)",
  },
  closeBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.28)",
    background: "transparent",
    color: "#fff",
    fontSize: 18,
    fontWeight: 800,
    cursor: "pointer",
  } as React.CSSProperties,
  body: {
    overflowY: "auto" as const,
    WebkitOverflowScrolling: "touch" as const,
    padding: 12,
  },
  bannerCol: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
    marginBottom: 20,
  },
  bannerImg: {
    width: "100%",
    height: "auto",
    borderRadius: 6,
    display: "block",
  },
  nav: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 8,
  },
  item: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 14px",
    borderRadius: 6,
    background: "#122538",
    color: "#eef4ff",
    fontWeight: 700,
    textDecoration: "none",
    border: "1px solid rgba(255,255,255,0.12)",
  } as React.CSSProperties,
  logoutBtn: {
    marginTop: 12,
    width: "100%",
    background: "#1e73be",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "10px 0",
    fontWeight: 700,
    cursor: "pointer",
  } as React.CSSProperties,
};

export default function SlideMenuPanel({ open, onClose }: Props) {
  const navigate = useNavigate();

  // ログアウト処理
  const handleLogout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });

      // Cookie(uid) 削除 → 認証状態クリア → /entryへ
      navigate("/entry");
    } catch (e) {
      console.error("logout failed:", e);
    } finally {
      onClose();
    }
  };

  // ESCで閉じる
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // 開いている間はbodyスクロール固定
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (open) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        role="presentation"
        onClick={onClose}
        aria-hidden={!open}
        style={{
          ...styles.backdrop,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
        }}
      />

      {/* Panel */}
      <aside
        style={styles.panel(open)}
        onClick={(e) => e.stopPropagation()}
        aria-label="スライドメニュー"
      >
        {/* ヘッダー */}
        <div style={styles.header}>
          <div style={{ fontWeight: 900, letterSpacing: "0.04em" }}>
            メニュー
          </div>
          <button style={styles.closeBtn} onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </div>

        {/* 本文 */}
        <div style={styles.body}>
          {/* テレボートバナー */}
          <div style={styles.bannerCol}>
            <a
              href="https://spweb.brtb.jp/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="テレボート会員登録はこちら！"
            >
              <img
                src="/assets/member/banner_teleboat.png"
                alt="テレボート会員登録はこちら！"
                style={styles.bannerImg}
                loading="lazy"
              />
            </a>
          </div>

          {/* メニュー */}
          <nav style={styles.nav}>
            <Link to="/" onClick={onClose} style={styles.item}>
              HOME
            </Link>
            <Link to="/mypage" onClick={onClose} style={styles.item}>
              マイページ
            </Link>
            <Link to="/contact" onClick={onClose} style={styles.item}>
              お問い合わせ
            </Link>
            <Link to="/faq" onClick={onClose} style={styles.item}>
              よくある質問
            </Link>
            <Link to="/terms" onClick={onClose} style={styles.item}>
              ご利用規約
            </Link>
            <Link to="/privacy" onClick={onClose} style={styles.item}>
              プライバシーポリシー
            </Link>
            <Link to="/kyotei-law" onClick={onClose} style={styles.item}>
              モーターボート競走法に関する特記事項
            </Link>
            <Link to="/law" onClick={onClose} style={styles.item}>
              特定商取引法に基づく表記
            </Link>
            <Link to="/affiliate" onClick={onClose} style={styles.item}>
              メンテナンス
            </Link>

            {/* ログアウト処理 */}
            <button onClick={handleLogout} style={styles.logoutBtn}>
              ログアウト
            </button>
          </nav>
        </div>
      </aside>
    </>
  );
}
